/**
 * leadPreferences — display helpers for lead preference fields.
 *
 * Origin: 2026-05-14 audit of the live Supabase `leads` table found that
 * `neighborhoods`, `neighborhood_other`, and `pets` are TEXT columns
 * collected via the Website public lead-intake form (see
 * Codex/Website/src/components/GetStartedModal.tsx). The DB stores them as
 * delimited strings, JSON-stringified arrays, or true arrays depending on
 * intake-form version — these helpers normalize all shapes for consistent
 * UI display.
 *
 * This file is the foundation for the upcoming display wiring that will
 * extend lead row cards, the convert-to-client modal, and the dashboard
 * lead popovers (in-flight on branch `wx/lnpro-account-management-admin`).
 * Helpers stay framework-free so they're equally usable from a row render,
 * a Convert modal pre-fill, or a server-side template substitution layer
 * if that lands later.
 *
 * Constraint per WX 2026-05-14: do NOT reference `first_name`, `last_name`,
 * `has_pets`, `pet_count`, or `pet_notes` in this module. Those columns do
 * not exist in prod Supabase yet (verified 2026-05-14 by live query). A
 * future migration will add them; helpers extend at that point.
 */

export interface LeadPreferenceSource {
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  neighborhoods?: unknown;
  neighborhood_other?: unknown;
  pets?: unknown;
}

export function buildLeadDisplayName(lead: LeadPreferenceSource): string {
  return (lead.name || "").trim() || "Unknown";
}

export function getLeadInitial(lead: LeadPreferenceSource): string {
  return buildLeadDisplayName(lead).charAt(0).toUpperCase() || "?";
}

/**
 * Accepts the wide range of shapes Supabase may return for a "list of
 * preferred neighborhoods" field: a true Postgres TEXT[] (Array<string>),
 * a JSON-stringified array, a comma/semicolon/pipe-delimited string, or
 * `null`/`undefined` for legacy leads. Returns a trimmed deduplicated
 * Array<string>.
 */
export function normalizeLeadNeighborhoods(value: unknown): string[] {
  if (Array.isArray(value)) {
    return dedupePreserveOrder(
      value.map((entry) => String(entry ?? "").trim()).filter(Boolean),
    );
  }

  if (typeof value !== "string") return [];
  const trimmed = value.trim();
  if (!trimmed) return [];

  if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return normalizeLeadNeighborhoods(parsed);
    } catch {
      // Fall through to delimiter parsing.
    }
  }

  return dedupePreserveOrder(
    trimmed
      .split(/[,;|]/)
      .map((entry) => entry.trim())
      .filter(Boolean),
  );
}

/**
 * `neighborhood_other` is a freeform fallback string captured when the
 * lead chose "Other" on the public intake form's neighborhoods picker.
 * Trim and return null when empty — display surfaces should treat null
 * as "no other-text provided".
 */
export function normalizeLeadNeighborhoodOther(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Joined-string render of the lead's neighborhood preferences. Suitable
 * for compact lead-card subtitles or single-line cell renders. When
 * `neighborhood_other` is present, appends `Other: {text}` as the final
 * segment per WX render spec 2026-05-14.
 *
 * Empty string return when the lead has no neighborhood data at all —
 * callers decide whether to fall through to a placeholder or hide the row.
 */
export function formatLeadLocation(
  neighborhoods: unknown,
  neighborhoodOther: unknown,
): string {
  const parts = buildLeadLocationParts(neighborhoods, neighborhoodOther);
  return parts.join(", ");
}

/**
 * Chip-array variant of `formatLeadLocation` — returns the same content
 * as a list so callers can render each piece as a separate Chip / Pill
 * component. The freeform "Other" entry is always last and labeled
 * `Other: {text}`. Useful for surfaces that want visual chip rendering
 * rather than a comma-joined string.
 */
export function buildLeadLocationParts(
  neighborhoods: unknown,
  neighborhoodOther: unknown,
): string[] {
  const parts = normalizeLeadNeighborhoods(neighborhoods);
  const other = normalizeLeadNeighborhoodOther(neighborhoodOther);
  if (other) parts.push(`Other: ${other}`);
  return parts;
}

export function formatLeadPets(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "Has pets" : "No pets";
  if (Array.isArray(value)) {
    return value
      .map((entry) => String(entry ?? "").trim())
      .filter(Boolean)
      .join(", ");
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return "";
    }
  }
  return String(value).trim();
}

export function buildLeadPreferenceNote(
  baseNotes: string | null | undefined,
  locationParts: string[],
  pets: string,
): string {
  const sections = [
    (baseNotes || "").trim(),
    locationParts.length
      ? `Preferred neighborhoods: ${locationParts.join(", ")}`
      : "",
    pets ? `Pets: ${pets}` : "",
  ].filter(Boolean);

  return sections.join("\n\n");
}

// ─────────────────────────────────────────────────────────────────────────
// internal helpers

function dedupePreserveOrder(items: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of items) {
    const key = item.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
}


/**
 * "Studio" · "2 Bed" — the renter's stated apartment size, tidied.
 *
 * Ch78: lifted VERBATIM out of ClientList, where it was a local const, so the
 * Leads page's "what they want" card and the lead rows read one definition of
 * a bedroom count instead of two that can drift apart.
 */
export function formatLeadApartmentSize(value?: string | null): string {
  const trimmed = value?.trim();
  if (!trimmed) return "";

  const normalized = trimmed
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");

  if (normalized.includes("studio")) return "Studio";

  const bedMatch = normalized.match(/(\d+)\s*(\+)?\s*(bedroom|bedrooms|beds|bed|br|bd)\b/);
  if (bedMatch) {
    return `${bedMatch[1]}${bedMatch[2] ? "+" : ""} Bed`;
  }

  return trimmed
    .split(" ")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}
